import React from "react";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";

const normalize = (value) => String(value || "").trim().toLowerCase();

const getPillMeta = (value) => {
  const status = normalize(value);

  if (!status) {
    return {
      className: "bg-gray-100 text-gray-500",
      icon: null,
      tooltip: "No value",
    };
  }

  if (["approved", "complete", "success", "active", "not-required", "not required"].includes(status)) {
    return {
      className: "bg-green-100 text-green-700",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 17 }} />,
      tooltip: "Approved / Complete / Not Required",
    };
  }

  if (["queued","pended"].includes(status)) {
    return {
      className: "bg-blue-100 text-blue-700",
      icon: <ScheduleOutlinedIcon sx={{ fontSize: 17 }} />,
      tooltip: "Queued / Pending",
    };
  }

  if (["running", "in progress", "in_progress"].includes(status)) {
    return {
      className: "bg-blue-100 text-blue-700",
      icon: <SyncOutlinedIcon sx={{ fontSize: 17 }} />,
      tooltip: "Running",
    };
  }

  if (["stopping"].includes(status)) {
    return {
      className: "bg-yellow-100 text-yellow-700",
      icon: <HourglassEmptyIcon sx={{ fontSize: 17 }} />,
      tooltip: "Stopping",
    };
  }

  if (["completed with errors", "completed_with_errors", "partial"].includes(status)) {
    return {
      className: "bg-amber-100 text-amber-700",
      icon: <WarningAmberIcon sx={{ fontSize: 17 }} />,
      tooltip: "Completed with errors",
    };
  }

  if (["completed", "completed successfully"].includes(status)) {
    return {
      className: "bg-green-100 text-green-700",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 17 }} />,
      tooltip: "Completed",
    };
  }

  if (["failed"].includes(status)) {
    return {
      className: "bg-red-100 text-red-700",
      icon: <ErrorOutlineIcon sx={{ fontSize: 17 }} />,
      tooltip: "Failed",
    };
  }

  if (["paused"].includes(status)) {
    return {
      className: "bg-slate-100 text-slate-700",
      icon: <PauseCircleOutlineIcon sx={{ fontSize: 17 }} />,
      tooltip: "Paused",
    };
  }

  if (["pending"].includes(status)) {
    return {
      className: "bg-yellow-100 text-yellow-700",
      icon: null,
      tooltip: "Pending",
    };
  }

  if (["error", "abnormal request", "exception error"].includes(status)) {
    return {
      className: "bg-red-100 text-red-700",
      icon: <ErrorOutlineIcon sx={{ fontSize: 17 }} />,
      tooltip: "Error",
    };
  }

  if (["rejected", "denied"].includes(status)) {
    return {
      className: "bg-red-100 text-red-700",
      icon: null,
      tooltip: "Rejected",
    };
  }

  if (["validation error", "validation_error"].includes(status)) {
    return {
      className: "bg-slate-100 text-slate-700",
      icon: <GppMaybeOutlinedIcon sx={{ fontSize: 17 }} />,
      tooltip: "Validation Error",
    };
  }

  if (["cancelled", "canceled"].includes(status)) {
    return {
      className: "bg-slate-100 text-slate-700",
      icon: null,
      tooltip: "Cancelled",
    };
  }

  return {
    className: "bg-gray-100 text-gray-700",
    icon: null,
    tooltip: value,
  };
};

function StatusOutcomePill({ value, variant = "label" }) {
  const meta = getPillMeta(value);
  const label = value ? String(value).replace(/_/g, " ").toUpperCase() : "---";
  const isIconOnly = variant === "icon";

  return (
    <Tooltip title={meta.tooltip} arrow placement="top">
      <span
        className={`inline-flex items-center justify-center rounded-md text-[12px] font-bold uppercase leading-4 ${meta.className} ${
          isIconOnly
            ? "h-[28px] min-w-[38px] px-2"
            : "min-w-[112px] gap-1.5 px-2.5 py-1"
        }`}
      >
        {isIconOnly ? meta.icon || <span>{label}</span> : <span>{label}</span>}
      </span>
    </Tooltip>
  );
}

export default StatusOutcomePill;
